import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import { Button } from "../../../../../components/buttons/button";
import { ContainerCard } from "../../../../../components/cards/containerCard";
import { Icon } from "../../../../../components/icons/icon";
import { TextInput } from "../../../../../components/inputs/textInput";
import { TitleSubLoader } from "../../../../../components/loaders/titleSubLoader";
import { TitleSub } from "../../../../../components/typography/titleSub";
import {
  formatToCurrency,
  formatToNumber,
  parseStringToNumber,
} from "../../../../../helperFunctions";
import {
  useGetCooperativeSharesConfigQuery,
  useUpdateSharesConfigMutation,
} from "../../../../../redux/features/cooperative/cooperative.api";
import { sharesConfigurationSchema } from "../../../../../validationSchema";

export const SharesConfiguration = () => {
  const [mode, setMode] = useState<"view" | "edit">("view");

  const {
    data: sharesConfig,
    isLoading: fetchingConfig,
    refetch: refetchConfig,
  } = useGetCooperativeSharesConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [updateShares, { isLoading: updatingShares }] =
    useUpdateSharesConfigMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    handleSubmit,
    resetForm,
    handleChange,
  } = useFormik({
    initialValues: {
      price: "",
      minAmount: "",
      sharesInterestRate: "",
    },
    validationSchema: sharesConfigurationSchema,
    onSubmit: async (data) => {
      const resp = await updateShares({
        InterestRate: parseStringToNumber(data.sharesInterestRate)!,
        InitialSharePrice: parseStringToNumber(data.price)!,
        MinShares: parseStringToNumber(data.minAmount)!,
      });

      if (resp.data?.success) {
        setMode("view");
        resetForm();
        refetchConfig();
      }
    },
  });

  useEffect(() => {
    if (sharesConfig?.data) {
      setFieldValue("price", sharesConfig.data.InitialSharePrice);
      setFieldValue("minAmount", sharesConfig.data.MinShares);
      setFieldValue("sharesInterestRate", sharesConfig.data.InterestRate);
    }
    // eslint-disable-next-line
  }, [sharesConfig]);

  return (
    <ContainerCard className="!px-0">
      <div
        className="
        w-full flex flex-row gap-4
        items-center justify-between
        mb-6 px-4 md:px-8
        "
      >
        <p className="font-bold text-base">Shares Settings</p>

        <Button
          text={mode === "edit" ? "Save" : "Edit"}
          onClick={() => {
            if (mode === "edit") {
              handleSubmit();
            } else {
              setMode("edit");
            }
          }}
          type="button"
          className="!min-w-[80px] !max-w-fit"
          size="small"
          loading={updatingShares}
          disabled={updatingShares}
          variation="primary"
        />
      </div>

      {/* Configuration Sub-body */}
      <div className="px-4 md:px-8 border-b py-3">
        {fetchingConfig ? (
          <TitleSubLoader />
        ) : (
          <>
            <TitleSub
              title="Initial Share Price"
              subtitle={"What’s the cooperative’s initial share price?"}
              subtitleClassName="!mt-0"
            />

            <div className="mt-3">
              {mode === "view" && (
                <div className="text-sm md:text-base text-pry-green-200 font-medium">
                  {sharesConfig?.data?.InitialSharePrice
                    ? `${formatToCurrency(
                        sharesConfig?.data?.InitialSharePrice,
                        { decimalPlaces: 2, currency: "NGN" }
                      )}`
                    : "No initial share price set"}
                </div>
              )}

              {mode === "edit" && (
                <NumericFormat
                  suffix={undefined}
                  prefix={undefined}
                  decimalScale={2}
                  thousandSeparator
                  allowLeadingZeros={false}
                  customInput={TextInput}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  name={"price"}
                  value={values.price}
                  placeholder="Minimum share price"
                  hasIcon={false}
                  iconSvg={undefined}
                  errors={touched?.price && errors?.price ? errors?.price : ""}
                  inputClass=""
                  className="!p-0 !pr-4"
                  autoComplete="off"
                  customIcon={
                    <div>
                      <Icon svg={"naira"} width={20} height={20} />
                    </div>
                  }
                  customIconPosition="left"
                />
              )}
            </div>
          </>
        )}
      </div>

      <div className="px-4 md:px-8 border-b py-3">
        {fetchingConfig ? (
          <TitleSubLoader />
        ) : (
          <>
            <TitleSub
              title="Minimum Shares To Buy"
              subtitle={
                "What’s the minimum amount of shares that can be bought?"
              }
              subtitleClassName="!mt-0"
            />

            <div className="mt-3">
              {mode === "view" && (
                <div className="text-sm md:text-base text-pry-green-200 font-medium">
                  {sharesConfig?.data?.MinShares
                    ? formatToNumber(sharesConfig?.data?.MinShares)
                    : "No amount set"}
                </div>
              )}

              {mode === "edit" && (
                <NumericFormat
                  suffix={undefined}
                  prefix={undefined}
                  decimalScale={0}
                  thousandSeparator
                  allowLeadingZeros={false}
                  customInput={TextInput}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  name={"minAmount"}
                  value={values.minAmount}
                  placeholder="Minimum share to buy"
                  hasIcon={false}
                  iconSvg={undefined}
                  errors={
                    touched?.minAmount && errors?.minAmount
                      ? errors?.minAmount
                      : ""
                  }
                  inputClass=""
                  className="!p-0 !pl-4"
                  autoComplete="off"
                  customIcon={
                    <div
                      className="
                  font-medium text-sm md:text-base
                  py-[14px] px-[16px]
                  bg-white
                  border border-pry-dark-green-300/10
                  rounded-e-lg
                  "
                    >
                      Shares
                    </div>
                  }
                  customIconPosition="right"
                />
              )}
            </div>
          </>
        )}
      </div>

      <div className=" px-4 md:px-8 border-b-0 py-3 ">
        {fetchingConfig ? (
          <TitleSubLoader />
        ) : (
          <>
            <TitleSub
              title="Interest Rate"
              subtitle={"Amount of interest due per period, on a share."}
              subtitleClassName="!mt-0"
            />

            <div className="mt-3">
              {mode === "view" && (
                <div className="text-sm md:text-base text-pry-green-200 font-medium">
                  {sharesConfig?.data?.InterestRate
                    ? `${formatToNumber(sharesConfig?.data?.InterestRate, {
                        decimalPlaces: 2,
                        style: "percent",
                      })}`
                    : "No rate set"}
                </div>
              )}

              {mode === "edit" && (
                <NumericFormat
                  suffix={undefined}
                  prefix={undefined}
                  decimalScale={2}
                  thousandSeparator
                  allowLeadingZeros={false}
                  customInput={TextInput}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  name={"sharesInterestRate"}
                  value={values.sharesInterestRate}
                  placeholder="Enter interest rate"
                  hasIcon={false}
                  iconSvg={undefined}
                  errors={
                    touched?.sharesInterestRate && errors?.sharesInterestRate
                      ? errors?.sharesInterestRate
                      : ""
                  }
                  inputClass=""
                  className="!p-0 !pr-4"
                  autoComplete="off"
                  customIcon={
                    <div
                      className="
                        font-medium text-sm md:text-base
                        py-[14px] px-[16px]
                        bg-white
                        border border-pry-dark-green-300/10
                        rounded-s-lg
                        "
                    >
                      %
                    </div>
                  }
                  customIconPosition="left"
                />
              )}
            </div>
          </>
        )}
      </div>
    </ContainerCard>
  );
};
